import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize } from 'rxjs';

import {
  AddWorkUnitFiles,
  CheckedQualityCheckItem,
  LocationOnPlan,
  ProjectWorkUnitInTime,
  WorkUnitDoneQualityCheckItem,
  WorkUnitFile,
  WorkUnitInTimeData,
  WorkUnitQaSummary,
  WorkUnitQualityCheckList,
} from '@shared/models';

import { BaseService } from '../base.service';
import { LoadersService } from '../loaders.service';

@Injectable({ providedIn: 'root' })
export class WorkUnitsService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  getWorkUnitInTimeSummary(loader: string, workUnitId: string, date: string): Observable<ProjectWorkUnitInTime> {
    this.loadersService.show(loader);
    return this.http
      .get<ProjectWorkUnitInTime>(`${this.baseUrl}/work-units/${workUnitId}/work-unit-in-time-summary/${date}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getWorkUnitQaSummary(workUnitId: string): Observable<WorkUnitQaSummary> {
    return this.http.get<WorkUnitQaSummary>(`${this.baseUrl}/work-units/${workUnitId}/qa-summary`);
  }

  updateWorkUnit(loader: string, workUnitDetails: WorkUnitInTimeData): Observable<unknown> {
    this.loadersService.show(loader);
    return this.http
      .put(`${this.baseUrl}/work-units`, workUnitDetails)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  approveDoneQualityCheckItem(
    loader: string,
    workUnitDoneQualityCheckItemIds: string[]
  ): Observable<WorkUnitDoneQualityCheckItem[]> {
    this.loadersService.show(loader);
    return this.http
      .put<
        WorkUnitDoneQualityCheckItem[]
      >(`${this.baseUrl}/work-units/done-quality-check-item/approve`, { workUnitDoneQualityCheckItemIds, comment: '' })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  rejectDoneQualityCheckItem(
    loader: string,
    workUnitDoneQualityCheckItemIds: string[],
    comment: string
  ): Observable<WorkUnitDoneQualityCheckItem[]> {
    this.loadersService.show(loader);
    return this.http
      .put<
        WorkUnitDoneQualityCheckItem[]
      >(`${this.baseUrl}/work-units/done-quality-check-item/reject`, { workUnitDoneQualityCheckItemIds, comment })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addWorkUnitBulkFiles(loader: string, item: AddWorkUnitFiles): Observable<string> {
    this.loadersService.show(loader);
    return this.http
      .put<string>(`${this.baseUrl}/work-units/file`, item)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addQualityCheckPicture(
    loader: string,
    file: File,
    workUnitId: string,
    notes: string,
    locationX: number,
    locationY: number,
    planId: string
  ): Observable<void> {
    this.loadersService.show(loader);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workUnitId', workUnitId);
    formData.append('notes', notes);
    formData.append('locationX', locationX.toString());
    formData.append('locationY', locationY.toString());
    formData.append('planId', planId);
    return this.http
      .post<void>(`${this.baseUrl}/work-units/quality-check-picture`, formData)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateQualityCheckPicture(
    id: string,
    workUnitId: string,
    notes: string,
    planLocation: LocationOnPlan
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/work-units/quality-check-picture`, {
      id: id,
      workUnitId: workUnitId,
      notes: notes,
      planLocation: planLocation,
    });
  }

  deleteQualityCheckPicture(loader: string, workUnitId: string, pictureId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .delete<void>(`${this.baseUrl}/work-units/${workUnitId}/quality-check-picture/${pictureId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addQualityCheckList(loader: string, command: WorkUnitQualityCheckList): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .post<void>(`${this.baseUrl}/work-units/quality-check-list`, command)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateQualityCheckList(
    loader: string,
    id: string,
    workUnitId: string,
    name: string,
    planLocation: LocationOnPlan,
    checkedDoneQualityCheckItems: CheckedQualityCheckItem[],
    uncheckedDoneQualityCheckItemsIds: string[]
  ): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .put<void>(`${this.baseUrl}/work-units/quality-check-list`, {
        id: id,
        workUnitId: workUnitId,
        name: name,
        planLocation: planLocation,
        checkedDoneQualityCheckItems: checkedDoneQualityCheckItems,
        uncheckedDoneQualityCheckItemsIds: uncheckedDoneQualityCheckItemsIds,
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  deleteQualityCheckList(loader: string, workUnitId: string, listId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .delete<void>(`${this.baseUrl}/work-units/${workUnitId}/quality-check-list/${listId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addWorkUnitFile(loader: string, file: File, workUnitId: string, notes: string): Observable<void> {
    this.loadersService.show(loader);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workUnitId', workUnitId);
    formData.append('notes', notes);
    return this.http
      .post<void>(`${this.baseUrl}/work-units/work-unit-file`, formData)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  updateWorkUnitFile(workUnitFile: WorkUnitFile): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/work-units/work-unit-file`, workUnitFile);
  }

  deleteWorkUnitFile(loader: string, workUnitId: string, fileId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .delete<void>(`${this.baseUrl}/work-units/${workUnitId}/work-unit-file/${fileId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  setWorkUnitProgress(
    loader: string,
    workUnitId: string,
    day: string,
    progress: number
  ): Observable<WorkUnitInTimeData> {
    this.loadersService.show(loader);

    return this.http
      .put<WorkUnitInTimeData>(`${this.baseUrl}/work-units/set-progress`, {
        workUnitId,
        day,
        progress,
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
